import { Route, Routes } from 'react-router';

import { AppLayout } from '../components';
import { ChangesPage } from '../areas/customer/ChangesPage';
import { CompanyScopeProvider } from '../areas/customer/CompanyScopeProvider';
import { CustomerOverviewPage } from '../areas/customer/CustomerOverviewPage';
import { MovementStatusPage } from '../areas/customer/MovementStatusPage';
import { PersonHistoryPage } from '../areas/customer/PersonHistoryPage';
import { RosterPage } from '../areas/customer/RosterPage';
import { SpreadsheetUploadPage } from '../areas/customer/SpreadsheetUploadPage';
import { AuditTrailPage } from '../areas/staff/AuditTrailPage';
import { CertificatesPage } from '../areas/staff/CertificatesPage';
import { CompaniesPage } from '../areas/staff/CompaniesPage';
import { CompanyDetailPage } from '../areas/staff/CompanyDetailPage';
import { EsocialEventsPage } from '../areas/staff/EsocialEventsPage';
import { GroupDetailPage } from '../areas/staff/GroupDetailPage';
import { GroupsPage } from '../areas/staff/GroupsPage';
import { InsurersPage } from '../areas/staff/InsurersPage';
import { EsocialPage } from '../areas/staff/EsocialPage';
import { MailboxPage } from '../areas/staff/MailboxPage';
import { NewCertificatePage } from '../areas/staff/NewCertificatePage';
import { NewCompanyPage } from '../areas/staff/NewCompanyPage';
import { NewContractPage } from '../areas/staff/NewContractPage';
import { NewCredentialPage } from '../areas/staff/NewCredentialPage';
import { NewGroupPage } from '../areas/staff/NewGroupPage';
import { NewInsurerPage } from '../areas/staff/NewInsurerPage';
import { NewStaffUserPage } from '../areas/staff/NewStaffUserPage';
import { ReviewQueuePage } from '../areas/staff/ReviewQueuePage';
import { StaffCredentialsPage } from '../areas/staff/StaffCredentialsPage';
import { StaffUsersPage } from '../areas/staff/StaffUsersPage';
import { SubmissionsPage } from '../areas/staff/SubmissionsPage';
import { SubmissionContractPage } from '../areas/staff/SubmissionContractPage';
import { SubmissionConsolidatedPage } from '../areas/staff/SubmissionConsolidatedPage';
import { SubmissionGroupPage } from '../areas/staff/SubmissionGroupPage';
import { StaffOverviewPage } from '../areas/staff/StaffOverviewPage';
import { StaffPersonPage } from '../areas/staff/StaffPersonPage';
import { LoginPage } from '../pages/LoginPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { AreaHome } from './AreaHome';
import { PATHS } from './paths';
import { RequireArea } from './RequireArea';
import { RequireSession } from './RequireSession';

/**
 * Every route, and the two conditions each one is behind.
 *
 * The nesting is the policy: `RequireSession` first, then `RequireArea`, then
 * the layout, then the screen. A screen added inside one of these branches
 * inherits both checks without doing anything, and a screen added outside them
 * is visibly outside them -- which is the difference between a guard and a
 * habit.
 */
function StaffArea({ children }: { children: React.ReactNode }) {
  return (
    <RequireSession>
      <RequireArea area="staff">
        <AppLayout area="staff">{children}</AppLayout>
      </RequireArea>
    </RequireSession>
  );
}

/**
 * `CompanyScopeProvider` sits inside both guards and outside every customer
 * screen, so "which company am I looking at?" is decided once, above all of
 * them, and no screen can answer it differently. It draws the layout itself,
 * because the header shows that company and carries the switcher. See
 * `areas/customer/companyScope.ts`.
 */
function CustomerArea({ children }: { children: React.ReactNode }) {
  return (
    <RequireSession>
      <RequireArea area="customer">
        <CompanyScopeProvider>{children}</CompanyScopeProvider>
      </RequireArea>
    </RequireSession>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path={PATHS.root} element={<AreaHome />} />
      <Route path={PATHS.login} element={<LoginPage />} />

      <Route
        path={PATHS.staff}
        element={
          <StaffArea>
            <StaffOverviewPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffReview}
        element={
          <StaffArea>
            <ReviewQueuePage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffSubmissions}
        element={
          <StaffArea>
            <SubmissionsPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffSubmissionContract}
        element={
          <StaffArea>
            <SubmissionContractPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffSubmissionConsolidated}
        element={
          <StaffArea>
            <SubmissionConsolidatedPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffSubmissionGroup}
        element={
          <StaffArea>
            <SubmissionGroupPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffMailbox}
        element={
          <StaffArea>
            <MailboxPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCompanies}
        element={
          <StaffArea>
            <CompaniesPage />
          </StaffArea>
        }
      />
      {/* Listed before the `:empresaId` route only for reading order: the
          router ranks a static segment above a dynamic one whatever the
          source says, so `/equipe/empresas/nova` can never be read as a
          company whose uuid is "nova". */}
      <Route
        path={PATHS.staffCompanyNew}
        element={
          <StaffArea>
            <NewCompanyPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCompany}
        element={
          <StaffArea>
            <CompanyDetailPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCompanyContractNew}
        element={
          <StaffArea>
            <NewContractPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCompanyPerson}
        element={
          <StaffArea>
            <StaffPersonPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffGroups}
        element={
          <StaffArea>
            <GroupsPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffGroupNew}
        element={
          <StaffArea>
            <NewGroupPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffGroup}
        element={
          <StaffArea>
            <GroupDetailPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffInsurers}
        element={
          <StaffArea>
            <InsurersPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffInsurerNew}
        element={
          <StaffArea>
            <NewInsurerPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCredentials}
        element={
          <StaffArea>
            <StaffCredentialsPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCredentialNew}
        element={
          <StaffArea>
            <NewCredentialPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCertificates}
        element={
          <StaffArea>
            <CertificatesPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffCertificateNew}
        element={
          <StaffArea>
            <NewCertificatePage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffEsocial}
        element={
          <StaffArea>
            <EsocialPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffEsocialEvents}
        element={
          <StaffArea>
            <EsocialEventsPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffUsers}
        element={
          <StaffArea>
            <StaffUsersPage />
          </StaffArea>
        }
      />
      <Route
        path={PATHS.staffUserNew}
        element={
          <StaffArea>
            <NewStaffUserPage />
          </StaffArea>
        }
      />

      <Route
        path={PATHS.staffAudit}
        element={
          <StaffArea>
            <AuditTrailPage />
          </StaffArea>
        }
      />

      <Route
        path={PATHS.customer}
        element={
          <CustomerArea>
            <CustomerOverviewPage />
          </CustomerArea>
        }
      />
      <Route
        path={PATHS.customerBeneficiaries}
        element={
          <CustomerArea>
            <RosterPage />
          </CustomerArea>
        }
      />
      <Route
        path={PATHS.customerPerson}
        element={
          <CustomerArea>
            <PersonHistoryPage />
          </CustomerArea>
        }
      />
      <Route
        path={PATHS.customerMovements}
        element={
          <CustomerArea>
            <ChangesPage />
          </CustomerArea>
        }
      />

      <Route
        path={PATHS.customerStatus}
        element={
          <CustomerArea>
            <MovementStatusPage />
          </CustomerArea>
        }
      />

      <Route
        path={PATHS.customerSpreadsheetUpload}
        element={
          <CustomerArea>
            <SpreadsheetUploadPage />
          </CustomerArea>
        }
      />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
